// Xbox (or any "standard" mapping) controller support through the Gamepad API.
// It drives the page's own buttons and cards, so every screen works with no
// controller-specific UI: the D-pad moves focus by on-screen position, A
// presses what's focused, and a few buttons are shortcuts.

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Spatial navigation: the candidate nearest to `from` in direction `dir`,
 * weighting sideways distance more than forward distance so that "right"
 * prefers the next thing on the same row. Returns -1 if there's nothing that way.
 */
export function pickNeighbor(from: Rect, candidates: readonly Rect[], dir: Direction): number {
  const cx = from.left + from.width / 2;
  const cy = from.top + from.height / 2;
  let best = -1;
  let bestScore = Infinity;
  candidates.forEach((r, i) => {
    const x = r.left + r.width / 2;
    const y = r.top + r.height / 2;
    const dx = x - cx;
    const dy = y - cy;
    const forward = dir === 'right' ? dx : dir === 'left' ? -dx : dir === 'down' ? dy : -dy;
    const sideways = dir === 'left' || dir === 'right' ? Math.abs(dy) : Math.abs(dx);
    // Must actually be that way (a few px of slack for elements on the same line).
    if (forward <= 4) return;
    const score = forward + sideways * 2.5;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  });
  return best;
}

/** Standard-mapping button indices. */
export const BUTTON = { A: 0, B: 1, X: 2, Y: 3, LB: 4, RB: 5, VIEW: 8, MENU: 9, UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15 } as const;
export type ButtonName = keyof typeof BUTTON;

const REPEAT_DELAY_MS = 380;
const REPEAT_EVERY_MS = 130;
const STICK_DEADZONE = 0.55;

interface PadLike {
  buttons: readonly { pressed: boolean }[];
  axes: readonly number[];
}

/**
 * Turns successive gamepad snapshots into presses: a press fires once when a
 * button goes down, and held directions repeat like a keyboard. Pure apart from
 * the timestamps it's given, so it's unit-tested.
 */
export class PadReader {
  private down = new Map<ButtonName, number>();
  private lastRepeat = new Map<ButtonName, number>();

  read(pad: PadLike, now: number): ButtonName[] {
    const held = new Set<ButtonName>();
    for (const name of Object.keys(BUTTON) as ButtonName[]) if (pad.buttons[BUTTON[name]]?.pressed) held.add(name);
    const [ax = 0, ay = 0] = pad.axes;
    if (ax <= -STICK_DEADZONE) held.add('LEFT');
    if (ax >= STICK_DEADZONE) held.add('RIGHT');
    if (ay <= -STICK_DEADZONE) held.add('UP');
    if (ay >= STICK_DEADZONE) held.add('DOWN');

    const fired: ButtonName[] = [];
    for (const name of held) {
      const since = this.down.get(name);
      if (since === undefined) {
        this.down.set(name, now);
        this.lastRepeat.set(name, now);
        fired.push(name);
      } else if (isDirection(name) && now - since >= REPEAT_DELAY_MS && now - this.lastRepeat.get(name)! >= REPEAT_EVERY_MS) {
        this.lastRepeat.set(name, now);
        fired.push(name);
      }
    }
    for (const name of [...this.down.keys()]) if (!held.has(name)) this.down.delete(name);
    return fired;
  }
}

function isDirection(name: ButtonName): name is 'UP' | 'DOWN' | 'LEFT' | 'RIGHT' {
  return name === 'UP' || name === 'DOWN' || name === 'LEFT' || name === 'RIGHT';
}

// ---------------------------------------------------------------------------
// DOM glue

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([type="hidden"]), select, [tabindex="0"]';

function visible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
}

/** What the D-pad can reach: everything in an open dialog, otherwise the whole page. */
function focusables(): HTMLElement[] {
  const scope = document.querySelector('[role="dialog"]') ?? document;
  return [...scope.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(visible);
}

function press(el: Element | null): boolean {
  if (!el) return false;
  // SVG cards have no .click(), so dispatch the event the page listens for.
  el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  return true;
}

function byTestId(id: string): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(`[data-testid="${id}"]`);
  return el && visible(el) ? el : null;
}

function focus(el: HTMLElement | null): void {
  if (!el) return;
  document.body.classList.add('pad-active');
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/** The most useful thing to land on when it becomes your turn. */
function bestStart(): HTMLElement | null {
  const pick = (sel: string) => [...document.querySelectorAll<HTMLElement>(sel)].find(visible) ?? null;
  return (
    pick('[data-testid="hand"] .card.called.clickable') ??
    pick('[data-testid="bid-panel"] [data-testid^="bid-"]:not([data-testid^="bid-suit"]):not([data-testid="bid-panel"]):not([data-testid="bid-0"])') ??
    pick('[data-testid="call-panel"] .card') ??
    pick('[data-testid="hand"] .card.clickable') ??
    pick('[data-testid="actions"] button:not([disabled])')
  );
}

function handCards(): HTMLElement[] {
  const cards = [...document.querySelectorAll<HTMLElement>('[data-testid="hand"] .card.clickable, [data-testid="kitty"] .card.clickable, [data-testid="call-panel"] .card')];
  return cards.filter(visible);
}

function move(dir: Direction): void {
  const all = focusables();
  const current = document.activeElement as HTMLElement | null;
  if (!current || current === document.body || !all.includes(current)) return focus(bestStart() ?? all[0] ?? null);
  const others = all.filter((el) => el !== current);
  const i = pickNeighbor(current.getBoundingClientRect(), others.map((el) => el.getBoundingClientRect()), dir);
  if (i >= 0) focus(others[i]!);
}

function cycleCards(step: 1 | -1): void {
  const cards = handCards();
  if (cards.length === 0) return;
  const at = cards.indexOf(document.activeElement as HTMLElement);
  focus(cards[at < 0 ? (step > 0 ? 0 : cards.length - 1) : (at + step + cards.length) % cards.length]!);
}

function back(): void {
  // Close the rules, finish the hand result, or undo the latest bid pick.
  const pressedBid = [...document.querySelectorAll<HTMLElement>('[data-testid="bid-panel"] [aria-pressed="true"]')].find(visible);
  press(byTestId('close-rules') ?? byTestId('next-hand') ?? pressedBid ?? null);
}

function toast(text: string): void {
  const el = document.createElement('div');
  el.className = 'pad-toast';
  el.setAttribute('role', 'status');
  el.setAttribute('data-testid', 'pad-toast');
  el.textContent = text;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4500);
}

function rumble(pad: Gamepad): void {
  const actuator = (pad as Gamepad & { vibrationActuator?: { playEffect?: (type: string, params: object) => Promise<unknown> } }).vibrationActuator;
  void actuator?.playEffect?.('dual-rumble', { duration: 140, strongMagnitude: 0.6, weakMagnitude: 0.3 })?.catch(() => {});
}

const SEEN_KEY = 'cyp.pad.seen';

/** Starts listening for controllers. Safe to call once at boot; does nothing without the API. */
export function startGamepad(): void {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return;
  const reader = new PadReader();
  let running = false;
  let wasMyTurn = false;

  const pad = (): Gamepad | null => [...navigator.getGamepads()].find((p): p is Gamepad => !!p && p.connected !== false) ?? null;

  const handle = (name: ButtonName, gp: Gamepad) => {
    switch (name) {
      case 'UP':
        return move('up');
      case 'DOWN':
        return move('down');
      case 'LEFT':
        return move('left');
      case 'RIGHT':
        return move('right');
      case 'A': {
        const el = document.activeElement as HTMLElement | null;
        if (el && el !== document.body) press(el);
        else focus(bestStart());
        return;
      }
      case 'B':
        return back();
      case 'X':
        return void press(byTestId('bid-0'));
      case 'Y':
        return void press(byTestId('close-rules') ?? byTestId('open-rules'));
      case 'LB':
        return cycleCards(-1);
      case 'RB':
        return cycleCards(1);
      case 'MENU':
        return void press(byTestId('next-hand') ?? byTestId('bid-submit') ?? byTestId('rematch') ?? byTestId('play-again') ?? byTestId('start-local') ?? byTestId('start-game'));
      case 'VIEW':
        return byTestId('log')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    void gp;
  };

  const tick = () => {
    const gp = pad();
    if (!gp) {
      running = false;
      return;
    }
    for (const name of reader.read(gp, performance.now())) handle(name, gp);
    const myTurn = !!byTestId('your-turn');
    if (myTurn && !wasMyTurn) {
      rumble(gp);
      const active = document.activeElement;
      // Jump to the useful control unless you're already somewhere in your panel.
      if (!active || active === document.body || !document.querySelector('[data-testid="actions"]')?.contains(active)) focus(bestStart());
    }
    wasMyTurn = myTurn;
    requestAnimationFrame(tick);
  };

  const begin = () => {
    if (running || !pad()) return;
    running = true;
    let seen = false;
    try {
      seen = localStorage.getItem(SEEN_KEY) === '1';
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Not remembered; show the hint again next time.
    }
    if (!seen) toast('Controller connected: D-pad to move, A to choose, B to go back, Y for rules.');
    focus(bestStart());
    requestAnimationFrame(tick);
  };

  window.addEventListener('gamepadconnected', begin);
  // A controller that was already connected before the page loaded shows up on its first button press.
  window.addEventListener('focus', begin);
  begin();
  // Mouse or touch use hides the controller focus ring again.
  window.addEventListener('pointerdown', () => document.body.classList.remove('pad-active'));
}
